import {
  Component, DestroyRef, EventEmitter, Input, OnChanges, Output,
  SimpleChanges, inject,
} from "@angular/core";
import { CoreCommonModule } from "@core/common.module";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from "@angular/forms";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { ActivatedRoute, Router } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { first } from "rxjs/operators";
import { CookieService } from "ngx-cookie";
import { Buffer } from "buffer";
import { resolveReturnUrl } from "@core/helpers/return-url.helpers";
import { serverErrorMessage } from "@core/helpers";
import { AuthenticationService } from "@core/services/auth";
import { HelperFunctionsService } from "@core/services/helper-functions.service";
import { SubscriptionService } from "@core/services/subscription.service";
import { SubscriptionDetails } from "@core/models/subscription";
import { User } from "@core/models";

export type LoginAccountType = "Student" | "Teacher" | "Parent";

/** The login card for a `UserCategory`: every staff category is a teacher; null when there is none. */
export function accountTypeOf(userCategory: string | null | undefined): LoginAccountType | null {
  if (!userCategory) return null;
  if (userCategory === "STUDENT") return "Student";
  if (userCategory === "PARENT") return "Parent";
  return "Teacher";
}

@Component({
  standalone: true,
  imports: [CoreCommonModule, NgbModule],
  selector: "app-login-form",
  templateUrl: "./login-form.html",
  styleUrls: ["./login-form.scss"],
})
export class LoginFormComponent implements OnChanges {
  @Input() selectedRole: string | null = null;
  @Input() userNameText = "Username or Phone Number";
  @Input() userNamePlaceholder = "Username/Phone Number";
  @Input() prefillUsername = "";

  @Output() usernameChange = new EventEmitter<string>();
  @Output() userCheckedChange = new EventEmitter<boolean>();
  @Output() forgotUsernameClick = new EventEmitter<void>();
  @Output() teacherForgotUsernameClick = new EventEmitter<void>();
  @Output() forgotPasswordClick = new EventEmitter<{
    username: string;
    userPhoneNumber: string;
    userCategory: string;
  }>();
  /** The account the username check found, when it isn't the card that was picked. */
  @Output() accountTypeDetected = new EventEmitter<LoginAccountType>();

  loginForm: UntypedFormGroup;
  submitted = false;
  loading = false;
  passwordTextType = false;
  usernameCheck = false;
  userChecked = false;
  error: any = {};
  feedback: any;
  showPasswordErrorAlert = true;
  showForgotUsernameInfo = false;
  userPhoneNumber = "";
  /** What `/check` says the account is ("TEACHER", "STUDENT", "PARENT"); the reset journey follows it. */
  userCategory = "";

  private storedUsername: string;
  private storedPassword: string;
  private contestMemberId: number;
  private returnUrl: string | null;

  private destroyRef = inject(DestroyRef);
  private _formBuilder = inject(UntypedFormBuilder);
  private _authenticationService = inject(AuthenticationService);
  private _cookieService = inject(CookieService);
  private _router = inject(Router);
  private _activatedRoute = inject(ActivatedRoute);
  private _helperFunctions = inject(HelperFunctionsService);
  private _subscriptionService = inject(SubscriptionService);

  constructor() {
    this.loginForm = this._formBuilder.group({
      username: ["", [Validators.required, this.userNameValidator]],
      password: ["", [Validators.required]],
      rememberMe: [false],
    });

    this.contestMemberId = this._activatedRoute.snapshot.queryParams.contestMemberId;
    this.returnUrl = resolveReturnUrl(this._activatedRoute.snapshot.queryParams["returnUrl"]);

    this.restoreRememberedLogin();

    this.f.username.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      this.usernameChange.emit(val);
    });
  }

  /** Prefills a remembered login; a `rememberMe` left without its username and password is cleared, not read. */
  private restoreRememberedLogin(): void {
    if (this._cookieService.get("rememberMe") !== "true") return;
    const username = this._cookieService.get("username");
    const password = this._cookieService.get("password");
    if (!username || !password) {
      this._cookieService.remove("rememberMe");
      return;
    }
    this.storedUsername = Buffer.from(username, "base64").toString("binary");
    this.storedPassword = Buffer.from(password, "base64").toString("binary");
    this.f.rememberMe.setValue(true);
    this.f.username.setValue(this.storedUsername);
    this.f.password.setValue(this.storedPassword);
    this.usernameChange.emit(this.storedUsername);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["prefillUsername"] && this.prefillUsername) {
      this.f.username.setValue(this.prefillUsername);
      this.usernameChange.emit(this.prefillUsername);
    }
    if (changes["selectedRole"]) {
      this.applyRoleValidators();
    }
  }

  private applyRoleValidators(): void {
    this.f.username.clearValidators();
    if (this.selectedRole === "Parent") {
      this.f.username.setValidators([Validators.required, Validators.pattern("^\\d{10}$")]);
    } else {
      this.f.username.setValidators([Validators.required]);
    }
    if (!this.userChecked) this.f.username.enable();
    this.f.username.updateValueAndValidity();
  }

  get f() {
    return this.loginForm.controls;
  }

  userNameValidator(control: AbstractControl): ValidationErrors | null {
    const username = control.value?.toLowerCase();
    const notANumber = isNaN(+Number(username));
    if (notANumber) {
      if (username?.match(/^(.*)+@(.*)+$/)) return null;
    } else {
      if (username?.match(/^\+?\d+$/)) return null;
    }
    return { format: true };
  }

  togglePasswordTextType(): void {
    this.passwordTextType = !this.passwordTextType;
  }

  checkUsername(): void {
    const username = this.f.username.value;
    if (!username) return;
    this.usernameCheck = true;
    this.clearAlerts();
    if (this.f.username.errors?.format) return;

    this.loading = true;
    this._authenticationService
      .checkUsernameOrPhone(username)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.clearAlerts();
          this.feedback = "Enter password to login";
          this.userPhoneNumber = res.phoneNumber || "";
          this.userCategory = res.userCategory || "";
          if (username !== this.storedUsername) {
            this.f.rememberMe.setValue(false);
            this.f.password.setValue("");
          }
          this.userChecked = true;
          this.userCheckedChange.emit(true);
          this.f.username.disable();
          const accountType = accountTypeOf(res.userCategory);
          if (accountType && accountType !== this.selectedRole) this.accountTypeDetected.emit(accountType);
        },
        error: (err) => {
          this.clearAlerts();
          this.loading = false;
          if (err?.error?.message?.toLowerCase().includes("username")) {
            this.f.username.setErrors({ notFound: true });
          } else {
            this.error.body = serverErrorMessage(err, "Couldn't check your username. Please try again.");
          }
          this.showForgotUsernameInfo = true;
        },
      });
  }

  onUsernameEnter(event: Event): void {
    event.preventDefault();
    if (!this.userChecked) {
      this.checkUsername();
    }
  }

  onSubmit(): void {
    this.submitted = true;
    // The three go together: a sign-in that isn't remembered must not leave `rememberMe` behind.
    this._cookieService.remove("username");
    this._cookieService.remove("password");
    this._cookieService.remove("rememberMe");
    if (this.loginForm.invalid || !this.userChecked) return;

    this.loading = true;
    this.loginUser(this.f.username.value.toLowerCase(), this.f.password.value);
  }

  private loginUser(username: string, password: string): void {
    const isSelfRegistered = /^[0-9 +]*$/.test(username);
    this._authenticationService
      .login(username, password, isSelfRegistered, this.contestMemberId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (usr) => {
          this.loading = false;
          if (this.f.rememberMe.value) {
            this._cookieService.put("username", Buffer.from(this.f.username.value).toString("base64"));
            this._cookieService.put("password", Buffer.from(this.f.password.value).toString("base64"));
            this._cookieService.put("rememberMe", JSON.stringify(true), { secure: true });
          }
          const isStudent = usr.role === 30 || usr.role === 31;
          if (isStudent) {
            this.getUserSubscriptionDates(usr);
            return;
          }
          this.redirectAfterLogin(usr);
        },
        error: (err) => {
          this.loading = false;
          this.feedback = "";
          this.error.title = "Error";
          if (err?.error?.message?.toLowerCase().includes("password")) {
            this.f.password.setErrors({ invalid: true });
            this.showPasswordErrorAlert = true;
          } else {
            this.error.body = serverErrorMessage(err, "Couldn't sign you in. Please try again.");
          }
        },
      });
  }

  /**
   * Students are sent on by way of their subscription, which decides onboarding versus dashboard.
   *
   * The redirect has to happen either way. By the time this runs the learner is already signed in
   * — the token is stored — so a failure here costs only that decision. Leaving them on the form
   * stranded them somewhere that looks like the login simply did nothing, and refreshing the page
   * took them to the dashboard, since the session was live all along.
   */
  private getUserSubscriptionDates(user: User): void {
    this._subscriptionService
      .getUserSubscriptionDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: SubscriptionDetails) => {
          if (!res.subjectsSelected) {
            this._router.navigate(["/onboarding"]);
            return;
          }
          this.redirectAfterLogin(user);
        },
        error: (err) => {
          // Guarded: on a network or CORS failure there is no err.error to read, and throwing in
          // here would lose the redirect below with it.
          console.error(err?.error?.message ?? err);
          this.redirectAfterLogin(user);
        },
      });
  }

  private redirectAfterLogin(user: User): void {
    this.returnUrl
      ? this._router.navigate([this.returnUrl])
      : this._helperFunctions.navigateToUsersDashboard(user);
  }

  clearAlerts(): void {
    this.error = {};
    this.feedback = null;
  }

  onForgotUsername(): void {
    this.forgotUsernameClick.emit();
  }

  onTeacherForgotUsername(): void {
    this.teacherForgotUsernameClick.emit();
  }

  onForgotPassword(): void {
    this.forgotPasswordClick.emit({
      username: this.f.username.value,
      userPhoneNumber: this.userPhoneNumber,
      userCategory: this.userCategory,
    });
  }

  reset(): void {
    this.userChecked = false;
    this.submitted = false;
    this.usernameCheck = false;
    this.showForgotUsernameInfo = false;
    this.error = null;
    this.userPhoneNumber = "";
    this.userCategory = "";
    this.f.username.enable();
    this.loginForm.reset();
    this.userCheckedChange.emit(false);
    this.usernameChange.emit("");
  }

  prefillAfterPasswordReset(username: string): void {
    this.f.username.setValue(username);
    this.f.username.enable();
    this.userChecked = true;
    this.userCheckedChange.emit(true);
    this.submitted = false;
    this.f.password.setValue("");
    this.f.password.setErrors(null);
    this.usernameChange.emit(username);
  }
}
